import { useTranslation } from 'react-i18next'

import {
  HStack,
  PrimaryButton,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'

type Props = {
  onCancel: () => void
  onSave: () => void
  isPending?: boolean
  /** true のとき保存ボタンを disabled にする (= Edit モーダルで fetch 中 / fetch エラー時)。 */
  isSaveDisabled?: boolean
  /**
   * 保存ボタンの押せない理由。 指定した時はボタンを disabled にし、 理由をツールチップで出す
   * (design/ui-feasibility/components/action-reason.md の区分 2)。
   */
  saveDisabledReason?: string
  /** true のときキャンセルボタンを disabled にする (= 保存 / 削除処理中でキャンセル不可の場合)。 */
  isCancelDisabled?: boolean
  saveLabel?: string
  cancelLabel?: string
  /** 保存処理中 (isPending) に PrimaryButton へ表示するテキスト (= 「保存中…」等)。 */
  saveLoadingText?: string
}

/**
 * モーダル footer の「キャンセル / 保存」アクション。
 * design (éClair) 準拠で TertiaryButton(キャンセル) + PrimaryButton(保存) に固定する。
 *
 * ボタン間隔は 8px (`gap={2}`)。design/css/components.css `.ecl-modal-footer`
 * (`gap: 8px`) が全モーダル共通の正。
 */
export const SaveCancelFooter = ({
  onCancel,
  onSave,
  isPending = false,
  isSaveDisabled = false,
  saveDisabledReason,
  isCancelDisabled = false,
  saveLabel,
  cancelLabel,
  saveLoadingText,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <HStack gap={2}>
      <TertiaryButton disabled={isCancelDisabled} onClick={onCancel}>
        {cancelLabel ?? t('common:action.cancel')}
      </TertiaryButton>
      <ButtonTooltip content={saveDisabledReason} focusable>
        <PrimaryButton
          disabled={isSaveDisabled || saveDisabledReason !== undefined}
          loading={isPending}
          loadingText={saveLoadingText}
          onClick={onSave}
        >
          {saveLabel ?? t('common:action.save')}
        </PrimaryButton>
      </ButtonTooltip>
    </HStack>
  )
}
